// Centralized SEO/Schema.org configuration for Talkani.
// IMPORTANT: The legal entity is in Gründung (i. Gr.) — not yet entered in the
// Handelsregister. We surface the "i. Gr." suffix everywhere the legal name is
// shown publicly (§ 11 Abs. 2 GmbHG). The "brandName" is used for short
// marketing copy where the legal designation isn't required.

export const BUSINESS_PROFILE = {
  // Marketing / brand-only short form (no GmbH suffix)
  brandName: "Talkani",
  // Trade name often shown next to the logo
  tradeName: "Talkani Logistik & Reinigung",
  // Legally-correct designation for the formation phase
  shortName: "Talkani GmbH i. Gr.",
  name: "Talkani Logistik & Reinigung GmbH i. Gr.",
  legalName: "Talkani Logistik & Reinigung GmbH i. Gr.",
  // Boolean flag — true while the entity is in formation (pre-HRB). When the
  // company gets entered in the Handelsregister, flip this to false and update
  // the names above to drop "i. Gr.".
  inFormation: true,
  url: "https://talkani.de",
  logo: "https://talkani.de/logo512.png",
  image: "https://talkani.de/generated/hero.png",
  telephone: "+49-175-8802646",
  email: "info@talkani.de",
  street: "Walddörferstraße 383",
  city: "Hamburg",
  postalCode: "22047",
  country: "DE",
  latitude: 53.601989,
  longitude: 10.135829,
  priceRange: "€€",
  foundingYear: 2026,
};

// Schema.org LocalBusiness — included on every page (renders once via SEOHead).
export const LOCAL_BUSINESS_SCHEMA = {
  "@context": "https://schema.org",
  "@type": ["LocalBusiness", "MovingCompany", "ProfessionalService"],
  "@id": `${BUSINESS_PROFILE.url}/#business`,
  name: BUSINESS_PROFILE.name,
  legalName: BUSINESS_PROFILE.legalName,
  url: BUSINESS_PROFILE.url,
  logo: BUSINESS_PROFILE.logo,
  image: BUSINESS_PROFILE.image,
  telephone: BUSINESS_PROFILE.telephone,
  email: BUSINESS_PROFILE.email,
  priceRange: BUSINESS_PROFILE.priceRange,
  foundingDate: String(BUSINESS_PROFILE.foundingYear),
  address: {
    "@type": "PostalAddress",
    streetAddress: BUSINESS_PROFILE.street,
    addressLocality: BUSINESS_PROFILE.city,
    postalCode: BUSINESS_PROFILE.postalCode,
    addressCountry: BUSINESS_PROFILE.country,
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: BUSINESS_PROFILE.latitude,
    longitude: BUSINESS_PROFILE.longitude,
  },
  areaServed: [
    { "@type": "City", name: "Hamburg" },
    { "@type": "City", name: "Bremen" },
    { "@type": "City", name: "Hannover" },
    { "@type": "City", name: "Lübeck" },
    { "@type": "City", name: "Kiel" },
    { "@type": "AdministrativeArea", name: "Norddeutschland" },
  ],
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
      opens: "00:00",
      closes: "23:59",
    },
  ],
  sameAs: ["https://wa.me/491758802646"],
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "Talkani Services",
    itemListElement: [
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Express-Lieferung & Transport" } },
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Umzug, Entrümpelung & Reinigung" } },
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Gebäudereinigung (alle Arten)" } },
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Hausreinigung & Privathaushalt" } },
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Gartenpflege & Gartenreinigung" } },
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Autoteile-Lieferung an Werkstätten" } },
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Regenrinnen-Reinigung mit Rohrkamera" } },
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Winterdienst" } },
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "E-Scooter Service & Battery Swap" } },
    ],
  },
};

export const ORGANIZATION_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": `${BUSINESS_PROFILE.url}/#organization`,
  name: BUSINESS_PROFILE.legalName,
  url: BUSINESS_PROFILE.url,
  logo: BUSINESS_PROFILE.logo,
  contactPoint: {
    "@type": "ContactPoint",
    telephone: BUSINESS_PROFILE.telephone,
    contactType: "customer service",
    areaServed: "DE",
    availableLanguage: ["German", "English", "Russian", "Ukrainian", "Arabic"],
  },
};

export const WEBSITE_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${BUSINESS_PROFILE.url}/#website`,
  url: BUSINESS_PROFILE.url,
  name: BUSINESS_PROFILE.shortName,
  inLanguage: ["de-DE", "en-GB", "ar"],
  publisher: { "@id": `${BUSINESS_PROFILE.url}/#organization` },
};

export function buildBlogPostSchema(post, lang, content) {
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: content?.title,
    description: content?.excerpt,
    image: `${BUSINESS_PROFILE.url}${post.cover}`,
    datePublished: post.date,
    dateModified: post.date,
    inLanguage: lang === "ar" ? "ar" : lang === "en" ? "en-GB" : "de-DE",
    author: { "@type": "Organization", name: BUSINESS_PROFILE.legalName, url: BUSINESS_PROFILE.url },
    publisher: { "@id": `${BUSINESS_PROFILE.url}/#organization` },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `${BUSINESS_PROFILE.url}/blog/${post.slug}`,
    },
  };
}
